import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './ghs-accordion-panel.css.lit';
import { OutlineAccordionPanel } from '../../base/outline-accordion-panel/outline-accordion-panel';
import '../ghs-icon/ghs-icon';
import { OutlineAccordion } from '../../base/outline-accordion/outline-accordion';
import { LightController } from '../../controllers/light-controller';

export type AccordionVariantType = 'primary' | 'secondary' | 'tertiary';

/**
 * The OutlineAccordionPanel component
 * @element ghs-accordion-panel
 * @slot heading: The title text for the panel component.
 * @slot default slot: The main panel content, visible when the panel is open.
 */
@customElement('ghs-accordion-panel')
export class GhsAccordionPanel extends OutlineAccordionPanel {
  static styles: CSSResultGroup = [componentStyles];

  lightController = new LightController(
    this,
    GhsAccordionPanel.styles,
    this.tagName.toLowerCase()
  );

  // Adds styles for a sidebar variant.
  @property({ reflect: true }) sidebar: Boolean;

  @property({ reflect: true }) variant: AccordionVariantType;

  firstUpdated() {
    super.firstUpdated();
    this.setPropsForBellin();
  }

  setPropsForBellin() {
    if (this.site === 'BH') {
      this.variant = 'primary';
    }
  }

  setAccordionIcon() {
    switch (true) {
      case this.site == 'GHS' && this.active:
        return 'accordion-collapse';
      case this.site == 'GHS' && !this.active:
        return 'accordion-expand';
      case this.site == 'BH' && this.active:
        return 'chevron-up';
      case this.site == 'BH' && !this.active:
        return 'chevron-down-solid';
      default:
        return 'hamburger';
    }
  }

  setActive() {
    const parentWrapper = this.parentElement as OutlineAccordion;
    const singlePanel = parentWrapper.singlePanel;

    if (singlePanel) {
      const activePanels = [...parentWrapper.panels].filter(
        panel => panel.active == true
      );

      if (activePanels.length < 1) {
        this.active = true;
      }

      if (activePanels.length > 0) {
        activePanels.map(panel => {
          if (panel.id === this.id) {
            this.active = false;
          } else {
            panel.active = false;
            this.active = true;
          }
        });
      }
    } else {
      this.active = !this.active;
    }

    // Added timeout to calculate the position after the panel is closed.
    // Keeping this code in case it's required back.
    /* setTimeout(() => {
      const pos = this.offsetTop + 100;
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }, 100); */
  }

  render(): TemplateResult {
    const isMobile = this.mobileController.isMobile ? 'mobile' : null;
    const isClean = this.clean ? 'clean' : null;
    const isActive = this.active ? 'active' : 'inactive';
    const sidebar = this.sidebar ? 'sidebar' : null;

    return html` <div
      class="accordion-panel ${this.variant} ${sidebar} ${isClean} ${isActive}"
    >
      <div class="accordion-heading">
        <button
          class="accordion-button ${isMobile} ${isActive} ${isClean}"
          id="${this.id}-button"
          aria-expanded=${this.active}
          aria-controls="${this.id}-info"
          @click=${this.setActive}
        >
          <span class="accordion-label ${isMobile}">
            <slot name="heading"> </slot>
          </span>
          <span class="accordion-icon ${isMobile} ${isActive} ${isClean}">
            <ghs-icon
              size=${this.site == 'BH' ? '22px' : '22px'}
              mobile-size=${this.site == 'BH' ? '18px' : '22px'}
              name="${this.setAccordionIcon()}"
            ></ghs-icon>
          </span>
        </button>
      </div>
      <div
        class="accordion-content ${isMobile}"
        role="region"
        aria-labelledby="${this.id}-button"
        id="${this.id}-info"
        aria-hidden="${!this.active}"
      >
        <slot></slot>
      </div>
    </div>`;
  }
}
